import {
  Component,
  AfterViewInit,
  ElementRef,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { StudentService } from '../../services/student.service';
import { OnboardingStateService } from '../../services/onboarding-state.service';
import { ModalComponent } from '../modal/modal.component';
import { ModalService } from '../../services/modal.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements AfterViewInit {
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private auth = inject(AuthService);
  private studentService = inject(StudentService);
  private onboardingState = inject(OnboardingStateService);
  private modalService = inject(ModalService);

  @ViewChild('emailInput') emailInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('loginFormEl') loginFormRef?: ElementRef<HTMLFormElement>;

  passwordVisible = false;
  isLoading = false;
  errorMessage = '';

  loginForm = this.fb.group({
    email: this.fb.control('', {
      validators: [Validators.required, Validators.email],
      nonNullable: true,
    }),
    password: this.fb.control('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
  });

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.emailInputRef?.nativeElement?.focus();
    }, 50);
  }

  handleFormKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const formEl = this.loginFormRef?.nativeElement;
    if (!formEl) return;
    const focusables = Array.from(
      formEl.querySelectorAll<HTMLElement>(
        'input:not([disabled]), button[type="submit"]:not([disabled])',
      ),
    );
    const idx = focusables.indexOf(event.target as HTMLElement);
    if (idx === -1) return;
    event.preventDefault();
    const nextIdx =
      event.key === 'ArrowDown'
        ? (idx + 1) % focusables.length
        : (idx - 1 + focusables.length) % focusables.length;
    focusables[nextIdx]?.focus();
  }

  openSimpleModal(title: string, msg: string) {
    const modalRef = this.modalService.open(ModalComponent);
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = msg;
  }

  SignIn() {
    this.errorMessage = '';
    if (this.loginForm.invalid) {
      this.errorMessage = 'Por favor ingresa tu correo y contraseña.';
      return;
    }

    const sign_in_mail = (this.loginForm.value.email || '').trim();
    const sign_in_pass = this.loginForm.value.password || '';
    this.isLoading = true;

    // Limpiar estado residual de sesiones anteriores para evitar cruces entre usuarios
    this.onboardingState.clearState();

    this.auth
      .signIn(sign_in_mail, sign_in_pass)
      .then(() => {
        this.isLoading = false;
        const user = this.auth.getCurrentUser();

        // 1. Si la cuenta está en dictamen (pending) o no aprobada (rejected), enviar al expediente
        if (
          user?.accountStatus === 'pending' ||
          user?.accountStatus === 'rejected'
        ) {
          this.onboardingState.setExpedienteState({
            accountStatus: user.accountStatus,
            folio: user.folio || this.onboardingState.state.expediente.folio,
            playCompletionSequence: false,
          });
          this.router.navigate(['/onboarding/expediente']);
          return;
        }

        // 2. Usuarios activos con acceso operativo
        if (user?.role === 'admin') {
          this.router.navigate(['/admin/home']);
        } else if (user?.role === 'volunteer') {
          this.router.navigate(['/volunteer-home']);
        } else {
          this.router.navigate(['/home']);
        }
      })
      .catch((error) => {
        this.isLoading = false;
        const backendMsg: string = error?.error?.message || '';
        if (backendMsg.toLowerCase().includes('pending')) {
          this.onboardingState.setExpedienteState({
            accountStatus: 'pending',
            playCompletionSequence: false,
          });
          this.router.navigate(['/onboarding/expediente']);
          return;
        }
        this.errorMessage = 'Correo electrónico o contraseña incorrectos.';
        setTimeout(() => {
          try {
            this.openSimpleModal(
              'Error de inicio de sesión',
              this.errorMessage,
            );
          } catch {
            // fallback handled by inline errorMessage
          }
        }, 0);
      });
  }

  navigateToSignup() {
    this.router.navigateByUrl('/auth/signup');
  }

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }
}
